/**
 * URL resolution based on frontend hostname.
 *
 * Backend mapping:
 *   dash.robo.harness       → https://api.harness
 *   dash.robo.dev.cosx.dev  → https://api.dev.cosx.dev
 *   dash.robo.test.cosx.dev → https://api.test.cosx.dev
 *   dash.robo.stag.cosx.dev → https://api.stag.cosx.dev
 *   dash.robo.cosx.co       → https://api.cosx.co
 *
 * Auth (OAuth) mapping:
 *   dash.robo.harness       → https://auth.cosx.harness
 *   dash.robo.dev.cosx.dev  → https://auth.dev.cosx.dev
 *   dash.robo.test.cosx.dev → https://auth.test.cosx.dev
 *   dash.robo.stag.cosx.dev → https://auth.stag.cosx.dev
 *   dash.robo.cosx.co       → https://auth.cosx.co
 *
 * Local / unknown hosts fall back to the dev environment.
 */

const BACKEND_URL_MAP: Record<string, string> = {
  'dash.robo.harness': 'https://api.harness',
  'dash.robo.dev.cosx.dev': 'https://api.dev.cosx.dev',
  'dash.robo.test.cosx.dev': 'https://api.test.cosx.dev',
  'dash.robo.stag.cosx.dev': 'https://api.stag.cosx.dev',
  'dash.robo.cosx.co': 'https://api.cosx.co',
}

const AUTH_URL_MAP: Record<string, string> = {
  'dash.robo.harness': 'https://auth.cosx.harness',
  'dash.robo.dev.cosx.dev': 'https://auth.dev.cosx.dev',
  'dash.robo.test.cosx.dev': 'https://auth.test.cosx.dev',
  'dash.robo.stag.cosx.dev': 'https://auth.stag.cosx.dev',
  'dash.robo.cosx.co': 'https://auth.cosx.co',
}

const DEV_BACKEND = 'https://api.dev.cosx.dev'
const DEV_AUTH = 'https://auth.dev.cosx.dev'

export function resolveBackendUrl(host: string): string {
  return BACKEND_URL_MAP[host] ?? DEV_BACKEND
}

export function resolveAuthBaseUrl(host: string): string {
  return AUTH_URL_MAP[host] ?? DEV_AUTH
}

/** Returns the backend base URL for the current environment. */
export function getBackendUrl(): string {
  // Explicit override always wins (useful for local dev)
  const override = import.meta.env.VITE_BACKEND_URL as string | undefined
  if (override !== undefined && override !== '') return override

  const host = window.location.hostname

  // Known cosx.dev hosts → use mapped backend
  if (BACKEND_URL_MAP[host]) return BACKEND_URL_MAP[host]

  // Unknown host (local dev, seasonsolt.myds.me, etc.) → relative URLs via Vite proxy
  return ''
}

/** Returns the OAuth/auth base URL for the current environment. */
export function getAuthBaseUrl(): string {
  const override = import.meta.env.VITE_AUTH_URL as string | undefined
  if (override) return override

  return resolveAuthBaseUrl(window.location.hostname)
}
